import type React from 'react';

export type BorderSide = 'top' | 'right' | 'bottom' | 'left';
export type StoredBorderSide = BorderSide | 'all';

export const ALL_BORDER_SIDES: readonly BorderSide[] = ['top', 'right', 'bottom', 'left'];

export type BorderSideInput = {
  borderSide?: unknown;
  borderSides?: unknown;
};

function isBorderSide(value: unknown): value is BorderSide {
  return value === 'top' || value === 'right' || value === 'bottom' || value === 'left';
}

/** Normalize legacy `borderSide` and new `borderSides` into selected sides or all. */
export function normalizeBorderSides(input?: BorderSideInput | null): BorderSide[] | 'all' {
  if (!input || typeof input !== 'object') return 'all';

  if (Array.isArray(input.borderSides)) {
    const selected = ALL_BORDER_SIDES.filter((side) => (input.borderSides as unknown[]).includes(side));
    if (selected.length === 0) return 'all';
    if (selected.length === ALL_BORDER_SIDES.length) return 'all';
    return selected;
  }

  const single = input.borderSide;
  if (single === 'all') return 'all';
  if (isBorderSide(single)) return [single];
  return 'all';
}

export function borderSidesToFormValue(input?: BorderSideInput | null): BorderSide[] {
  const normalized = normalizeBorderSides(input);
  return normalized === 'all' ? [...ALL_BORDER_SIDES] : normalized;
}

export function formBorderSidesToStored(values: Record<string, unknown>): {
  borderSide?: StoredBorderSide;
  borderSides?: BorderSide[];
} {
  const rawSides = values.borderSides;
  let selected: BorderSide[];
  if (Array.isArray(rawSides)) {
    selected = ALL_BORDER_SIDES.filter((side) => rawSides.includes(side));
  } else {
    selected = borderSidesToFormValue({ borderSide: values.borderSide });
  }

  if (selected.length === 0 || selected.length === ALL_BORDER_SIDES.length) {
    return { borderSide: 'all' };
  }
  if (selected.length === 1) {
    return { borderSide: selected[0] };
  }
  return { borderSides: selected };
}

export function applyBorderValueToSides(
  out: React.CSSProperties,
  borderValue: string,
  sides: BorderSide[] | 'all',
): void {
  if (sides === 'all') {
    out.border = borderValue;
    return;
  }
  for (const side of sides) {
    if (side === 'top') out.borderTop = borderValue;
    else if (side === 'right') out.borderRight = borderValue;
    else if (side === 'bottom') out.borderBottom = borderValue;
    else if (side === 'left') out.borderLeft = borderValue;
  }
}
